import React, {ChangeEvent, CSSProperties, FocusEvent, KeyboardEvent, MouseEvent, ReactNode, useLayoutEffect, useRef, useState} from "react";
import {genOnKeys, onKeyHandler, timePickerFrmt} from "./CommonHtml";
import {componentInit, tailCheck, useRerender, useStateEx} from "./CommonReact";
import {icn} from "./UIIcons";
import {funcOrValOr, makeArr} from "./CommonJS";
import {genTooltip} from "./Tooltip";

interface ButtonProps {
    h?: ReactNode | string;
    onClick?: (e: MouseEvent<HTMLDivElement>) => void;
    disabled?: boolean | string;
    disabledTitle?: string;
    title?: string;
    className?: string;
    tabIndex?: number;
    onKeys?: Record<string, (e: KeyboardEvent, unhandle: () => void) => void>;
    children?: ReactNode;
    noDot?: boolean;
    classNameDot?: string;
    [key: string]: any;
}

export const Button = ({h, onClick, disabled, ref, disabledTitle, title, className, tabIndex, onKeys, children,
    noDot, classNameDot, ...props}: ButtonProps) =>
    <div
        className={tailCheck(className, "flex whitespace-nowrap transition-opacity [transition-property:text-shadow]",
                             "select-none", "rounded-lg", "items-center",
                             "mr-1",         // allow disabled dot to fit within flex sizing - as absolute doesn't count.
                             "outline-none focus-visible:outline-offset-[-1px] focus-visible:outline-[1px] focus-visible:outline-tx-steel",
                             onClick && !disabled && "shadow-subtitle2 hover:[text-shadow:_-1px_0_0_var(--tw-shadow-color)]", "relative",
                             disabled ? "opacity-50 cursor-not-allowed" : onClick && !className?.includes?.("cursor-") && "cursor-pointer")}
        {...!disabled && {
            onClick: !onClick? undefined : e => (e.stopPropagation(), onClick(e)),
            tabIndex: onClick ? tabIndex || 0 : undefined,
            ...genOnKeys({Enter: onClick || undefined, " ": onClick || undefined, ...onKeys}),
        }}
        {...genTooltip(disabled && (disabledTitle || (typeof disabled === "string" && disabled)) || title)}
        {...props} ref={ref}>
        {h ?? children}
        {!noDot && (typeof disabled === "string" || (disabled && disabledTitle)) &&
            <div
                className={tailCheck(classNameDot, "absolute", "bg-sec", "rounded-full", "top-0.5 -right-1", "w-1 h-1")}
                style={classNameDot? undefined : getRelativeFromPadding(className)}/>}
    </div>;


const pDefs: Record<string, string> = {
    "px": "right",
    "py": "top",
};

function getRelativeFromPadding(className?: string): CSSProperties {
    if (!className) return {};
    const pMatches = [...className.matchAll(/\b(p[xy]?-?\d*)\b/g)].map(m => m[0]);
    return pMatches.map(cl => cl.split("-"))
        .reduce((o: CSSProperties, [p, val]: [string, string]) => {
            o[pDefs[p]] = `${(parseInt(val)-(p === "px" ? 2 :0))/4}rem`;
            return o;
        }, {});
}

interface ButtonAFProps extends ButtonProps {
    autoFocus?: boolean;
}

export function ButtonAF({autoFocus, ...props}: ButtonAFProps) {
    const rEl = useRef<HTMLDivElement>(null);
    // Use a callback ref that stores the element in rEl.current and focuses it only once
    return <Button
        {...(props)}
        ref={el => {
            if (autoFocus && el && !rEl.current) {
                rEl.current = el;
                setTimeout(() => {
                    // Check if still mounted and focus is possible
                    if (rEl.current === el && typeof el.focus === "function")
                        el.focus();

                }, 50);
            } else if (!autoFocus)
                rEl.current = undefined;
        }}/>;
}

interface InputChangeReturn<T> {
    inp: T;
    setInp: React.Dispatch<React.SetStateAction<T>>;
    onChange: (field: keyof T, value: any) => void;
    resetInput: () => T;
}

export function useInputChange<T extends Record<string, any>>(init?: T, overInit?: Partial<T>): InputChangeReturn<T> {
    const vals = useRef<{init: T}>({init: init || {} as T}).current;        // ensure actual original init values are used for resetInput.
    const [inp, setInp] = useState<T>({...init || {} as T, ...overInit} as T);

    return {
        inp, setInp,
        onChange: (field: keyof T, value: any) => { const v = funcOrValOr(value, inp[field]); setInp(inp => inp[field] === v? inp : ({...inp, [field]: v}));},
        resetInput: () => (setInp({...vals.init}), {...vals.init})           // return newest values for quick use
    };
}
type Booleanish = boolean | "true" | "false";

interface TextInputProps {
    field?: string;
    value?: string | number;
    onChange: (field: any, value: any) => void;
    placeholder?: string;
    validate?: (value: string | number) => string | undefined;
    type?: string;
    tag?: string;
    classNameWrapper?: string;
    className?: string;
    addClear?: boolean;
    btnRight?: ReactNode;
    onKeys?: Record<string, (e: KeyboardEvent, unhandle: () => void) => void>;
    children?: ReactNode;
    dataTest?: string;
    max?: number | string;
    autoComplete?: string;
    spellCheck?: Booleanish;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    [key: string]: any;
}

// text input - the component version
export const TextInput = ({field, ref, value, onChange, placeholder, validate, type, tag, classNameWrapper, className, addClear,
    btnRight, onKeys, children, dataTest, ...props}: TextInputProps) => {
    const rerender = useRerender();
    const vals = useRef<{prevVal?: string | number, err?: string}>({}).current;
    const [isPasswordShown, setIsPasswordShown] = useState(false);
    const rBtns = useRef<HTMLDivElement>(null);

    if (vals.prevVal !== value && vals.err)
        vals.err = validate?.(value || "");
    vals.prevVal = value;

    useLayoutEffect(() => {
        const inp = rBtns.current?.previousSibling as HTMLInputElement, wBtns = rBtns.current?.clientWidth;
        if (inp)
            inp.style.paddingRight = wBtns && ((wBtns + 10) + "px");
    }, [addClear, btnRight]);

    return (
        <div className={`relative ${classNameWrapper}`}>
            <input
                data-test={`${dataTest || field}-input`}
                className={`block leading-tight appearance-none transition ${
                    tailCheck(className, "rounded-xl", "bg-bg3", "text-sm text-tx-cool-grey", "border focus:ring-prm-main", "placeholder:italic",
                              "focus-visible:-outline-offset-1", "px-3.5", "py-[0.45rem]", "w-full", vals.err ? "border-sec" : "border-tx-steel")}`}
                type={(type !== "password" || !isPasswordShown) && type || "text"}
                value={value ?? ""}
                placeholder={placeholder}
                ref={ref}
                autoComplete={props.autoComplete ?? makeArr(8).map(() => String.fromCharCode(97 + Math.random() * 26 | 0)).join("")}   // trick browser's autofill
                spellCheck={props.spellCheck ?? "false"}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onChange(field,
                             type !== "number" ? e.target.value
                                 : (props.max && +e.target.value > +props.max) ? props.max : +e.target.value)}
                {...(onKeys || addClear) && genOnKeys(onKeys ?? {Escape: (_: KeyboardEvent, unhandle: () => void) => !value ? unhandle() : onChange(field, "")})}
                {...props}
                {...!validate || vals.err ? {} : {onBlur: (e: FocusEvent<HTMLInputElement>) => ((vals.err = validate?.(value || "")), vals.err && rerender(), props.onBlur?.(e))}}/>
            {(addClear || btnRight || type === "password") &&
                <div ref={rBtns} className={`absolute inset-y-0 ${type === "number" ? "right-8" : "right-1"} h-full flex items-center text-title`}>
                    {addClear && value &&
                        <div className="cursor-pointer text-[0.8rem] p-1" onClick={() => onChange(field, "")}>{icn.remove3}</div>}
                    {btnRight}
                    {type === "password" &&
                        <Button onClick={() => setIsPasswordShown(prevVal => !prevVal)} className={isPasswordShown ? "icon-eye-blocked" : "icon-eye"}/>}
                </div>}
            {children}
            {!!vals.err && <p className="text-sec text-xs italic">{vals.err}</p>}
        </div>
    );
};

interface EditableTextProps {
    normal: ReactNode;
    initText: string;
    classNameWrapper?: string;
    className?: string;
    onEdit: (text?: string) => void;
}

export function EditableText({normal, initText, classNameWrapper, className, onEdit}: EditableTextProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState(initText);
    const onEditDone = () => (onEdit(text), setIsEditing(false));

    return (
        <div onClick={(e: MouseEvent) => {
            e.stopPropagation();
            setIsEditing(true);
        }}>
            {!isEditing? normal :
                <TextInput
                    classNameWrapper={classNameWrapper} className={className} value={text} onChange={(_, v) => setText(v as string)} autoFocus
                    onBlur={onEditDone} onKeys={{Enter: onEditDone}} btnRight={
                        <div
                            className="cursor-pointer dark:text-red-500"
                            onMouseDown={(e: MouseEvent) => (e.preventDefault(), e.stopPropagation())}
                            onClick={() => onEdit()}>
                            {icn.remove3}
                        </div>}/>
            }
        </div>
    );
}

interface Select1Props {
    field: string;
    opts?: string[];
    optVals?: any[];
    sel?: any;
    idxSel?: number;
    onChange: (field: string, value: any) => void;
    onClick?: (field: string) => void;
    item0?: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    [key: string]: any;
}

// Can work either by index or by value. governed if given optVals. onChange will be given a value instead of index.
//      optVals: value corresponding to each item in opts.
//      sel: may give current value.
//
type RequireOptsOrOptVals =
    | ({opts: string[]; optVals?: undefined})
    | ({opts?: undefined; optVals: any[]});

type Select1PropsRequired = Omit<Select1Props, "opts" | "optVals"> & RequireOptsOrOptVals;

export function Select1({field, opts, optVals, sel, idxSel, onChange, onClick, item0, placeholder, disabled, className, ...props}: Select1PropsRequired) {
    let optionsArr = opts ?? optVals!;
    if (item0) optionsArr = [item0, ...optionsArr];

    return (
        <select
            id={field} disabled={disabled}
            value={
                idxSel ??
                ((optVals ?? opts).indexOf(sel) + (item0 ? 1 : 0))
            }
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                onChange(field, optVals ? optVals[parseInt(e.target.value) - (item0 ? 1 : 0)] : parseInt(e.target.value) | 0)
            }
            className={`bg-bg1 rounded-lg leading-6 px-2.5 py-1.5 border focus-visible:-outline-offset-1 border-tx-steel ${className}`}
            {...(onClick ? {onClick: () => onClick(field)} : {})} {...props}>
            {optionsArr.map((opt, i) => (
                <option key={i} value={i} hidden={!!(placeholder && !i)} className={`${ i || !item0 ? "" : "font-italic" } leading-10 py-1 px-2`}>
                    {opt}
                </option>
            ))}
        </select>
    );
}

interface ToggleButtonProps {
    h?: ReactNode;
    field: string;
    isChecked: boolean;
    onChange: (field: string, value: boolean) => void;
    className?: string;
    [key: string]: any;
}

export const ToggleButton = ({h, field, isChecked, onChange, className, ...props}: ToggleButtonProps) =>
    <Button
        {...{h, ...props}} className={tailCheck(className, !isChecked? "opacity-50 hover:opacity-75" : "text-title")}
        onClick={() => onChange(field, !isChecked)}/>;

interface MkCheckProps {
    className?: string;
}

export const MkCheck = ({className}: MkCheckProps) =>
    <svg
        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
        className={className?.match?.(/(^| )w-/)? className : "w-4 h-4"}>
        <path d="M4.5 12.75l6 6 9-13.5"/>
    </svg>;

interface MkCheckboxProps {
    isChecked: boolean;
    className?: string;
    onClick?: (e: MouseEvent) => void;
    [key: string]: any;
}

export const MkCheckbox = ({isChecked, className, ...props}: MkCheckboxProps) =>
    <div
        data-test="mk-checkbox" data-state={isChecked} className={
            tailCheck(className, "w-5 h-5", "shrink-0 transition-opacity flex items-center justify-center",
                      "border border-tx-steel bg-bg1", "rounded",
                      "outline-none focus-visible:outline-offset-[0px] focus-visible:outline-[1px] focus-visible:outline-tx-steel",
                      props.onClick && "cursor-pointer hover:opacity-70")}
        {...props}>
        {isChecked && <MkCheck {...{className}}/>}
    </div>;

interface CheckboxRawProps {
    field: string;
    isChecked: boolean;
    onChange?: (field: string, value: boolean) => void;
    [key: string]: any;
}

export function CheckboxRaw({field, isChecked, onChange, ...props}: CheckboxRawProps) {
    const onClick = (e: MouseEvent) =>  (e.stopPropagation(), onChange?.(field, !isChecked));
    return <MkCheckbox tabIndex={0} onClick={onChange && onClick} {...genOnKeys({" ": onChange && onClick})} {...{isChecked, ...props}}/>;
}

interface CheckboxProps {
    field?: string;
    isChecked?: boolean;
    value?: boolean;
    onChange: (field: string, value: boolean) => void;
    label?: ReactNode;
    className?: string;
    classNameLabel?: string;
    classNameCB?: string;
    disabled?: boolean;
    [key: string]: any;
}

export function Checkbox({field, isChecked, value, onChange, label, className, classNameLabel, classNameCB, disabled, ...props}: CheckboxProps) {
    return (
        <div
            className={tailCheck(className, "flex items-center", "gap-2.5", disabled? "opacity-30" : "cursor-pointer")}
            onClick={(e: MouseEvent) => !disabled && (e.stopPropagation(), onChange(field, !(isChecked || value)))}
            {...props}>
            <CheckboxRaw isChecked={isChecked || value || false} {...{field}} onChange={!disabled? onChange : undefined} className={classNameCB}/>
            <div className={classNameLabel}>{label}</div>
        </div>
    );
}

interface RadioProps {
    field: string;
    value: any;
    isChecked?: boolean;
    isDisabled?: boolean;
    selected?: any;
    onChange: (field: string, value: any) => void;
    onClick?: (field: string) => void;
    label: ReactNode;
    col1?: string;
    col2?: string;
    extra?: ReactNode;
    blob?: ReactNode | ((checked: boolean) => ReactNode);
}

export function Radio({field, value, isChecked, isDisabled, selected, onChange, label, col1, col2, extra, blob}: RadioProps) {
    const checked = !!isChecked || value === selected;
    return (
        <div className="flex items-baseline">
            <div className="flex-none w-6">
                <input
                    type="radio" className="dark:accent-cyan-700 outline-0" name={field} checked={checked} disabled={isDisabled}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => e.target.checked && onChange(field, value)}/>
            </div>
            {blob ?
                <div className="grow">{typeof blob === "function"? blob(checked) : blob}</div>
                :
                <>
                    <div className={`flex flex-none ${col1}`} onClick={() => onChange(field, value)}>
                        {extra && typeof label === "string"? label+ ":" : label}
                    </div>
                    {extra && <div className={`flex min-w-0 ${col2 || "grow"} ${!checked && "opacity-50"}`}>{extra}</div>}
                </>
            }
        </div>
    );
}

interface RadioOption {
    k: any;
    h: ReactNode;
    isDisabled?: boolean;
}

interface RadiosProps {
    field: string;
    opts?: RadioOption[];
    selected?: any;
    onChange: (field: string, value: any) => void;
    className?: string;
    onKeys?: Record<string, (e: KeyboardEvent, unhandle: () => void) => void>;
    autoFocus?: boolean;
}

export function Radios({field, opts, selected, onChange, className, onKeys, autoFocus}: RadiosProps) {
    return (
        <div className={`flex flex-col ${className}`} onKeyDown={onKeys && ((e: KeyboardEvent) => onKeyHandler(e, onKeys))}>
            {opts?.map((opt, i) =>
                <label key={opt.k} onClick={() => onChange(field, opt.k)} className="flex">
                    <div className="flex-none w-6">
                        <input
                            type="radio" className="dark:accent-cyan-700 outline-0"
                            name={field} checked={opt.k === selected} disabled={opt.isDisabled} autoFocus={!i && autoFocus}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => e.target.checked && onChange(field, opt.k)}/>
                    </div>
                    {opt.h}
                </label>
            )}
        </div>
    );
}

interface FormLabelProps {
    label: ReactNode;
    classNameCol1?: string;
    classNameCol2?: string;
    children: ReactNode;
    className?: string;
    dataTest?: string;
    [key: string]: any;
}

export function FormLabel({label, classNameCol1, classNameCol2, children, className, dataTest="global", ...props}: FormLabelProps) {
    return (
        <div data-test={dataTest} className={`flex items-center ${className}`} {...props}>
            <div data-test={`${dataTest}-form-label-title`} className={`flex-none ${classNameCol1}`}>{label}</div>
            <div data-test={`${dataTest}-form-label-value`} className={`min-w-0 ${classNameCol2}`}>{children}</div>
        </div>
    );
}

export const isValidRangeDateStr = (s: string): boolean =>
    !!s && /^\d{4}-\d{2}-\d{2}$/.test(s) && /^19|^20/.test(s) && !isNaN(new Date(s).getTime());

export const DateRangeInputs = ({val, onChange, field}) => {
    const fromStr = val?.from || "", toStr = val?.to || "";
    const today = timePickerFrmt();
    const update = (which: "from" | "to", v: string) =>
        onChange(field, which === "from" ? {from: v || undefined, to: toStr || undefined} : {from: fromStr || undefined, to: v || undefined});
    const validateFrom = (v: string) =>
        !v ? undefined : !isValidRangeDateStr(v) ? "Invalid date" : v > timePickerFrmt() ? "Date cannot be in the future" : undefined;

    const validateTo = (v: string) =>
        validateFrom(v) || (fromStr && v < fromStr ? "Must be on or after From date" : undefined);

    return <div className="flex flex-col gap-2 p-1 min-w-48">
        <div className="flex flex-col gap-1">
            <label className="text-xs text-tx-steel">From</label>
            <TextInput
                field="from" value={fromStr} onChange={(_, v) => update("from", v)}
                type="date" min="1980-01-01" max={today} placeholder="YYYY-MM-DD" className="w-full" validate={validateFrom}/>
        </div>
        <div className="flex flex-col gap-1">
            <label className="text-xs text-tx-steel">To</label>
            <TextInput
                field="to" value={toStr} onChange={(_, v) => update("to", v)}
                type="date" min={fromStr || undefined} max={today} placeholder="YYYY-MM-DD" className="w-full" validate={validateTo}/>
        </div>
    </div>;
};


interface RangeSliderProps {
    min?: number;                                                   // Axis start (default 0).
    max: number;                                                    // Axis end.
    start: number;                                                  // Lower thumb value.
    end: number;                                                    // Upper thumb value.
    step?: number;                                                  // Granularity (default 1).
    minGap?: number;                                                // Minimum distance between thumbs (default 0).
    maxGap?: number;                                                // Maximum window; sliding one thumb past it pushes the other along.
    onChange: (range: {start: number, end: number}) => void;       // Committed on pointer/key release, not per step.
    renderLabels?: (range: {start: number, end: number}) => ReactNode;  // Optional row beneath the track; gets the live values.
    className?: string;                                             // Width/spacing override (default w-[15rem]).
    dataTest?: string;
}

const rangeInputCls = "absolute inset-x-0 w-full bg-transparent appearance-none pointer-events-none "
    + "[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none "
    + "[&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full "
    + "[&::-webkit-slider-thumb]:bg-prm-light [&::-webkit-slider-thumb]:cursor-pointer "
    + "[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:w-3.5 "
    + "[&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-prm-light [&::-moz-range-thumb]:cursor-pointer";

// Two-thumb range slider. Drag a thumb to move one end, or drag the middle bar to slide the whole
// window while preserving its width. Values are committed on release so consumers don't churn per step.
export function RangeSlider({min = 0, max, start, end, step = 1, minGap = 0, maxGap, onChange, renderLabels,
    className, dataTest}: RangeSliderProps) {
    const span = max - min || 1;                                   // Guard against a zero-width axis (max === min).
    const clamp = (v: number) => Math.min(max, Math.max(min, v));

    const {state: st, setState} = useStateEx({s: clamp(start), e: clamp(end)});
    if (componentInit(st, [start, end], "sync")) Object.assign(st, {s: clamp(start), e: clamp(end)});   // Resync (clamped) when controlled props change.

    const onStart = (v: number) => {                               // Keep left of end; if window exceeds maxGap, pull the end along.
        const s = clamp(Math.min(v, st.e - minGap));
        setState({s, e: maxGap === undefined ? st.e : Math.min(st.e, s + maxGap)});
    };
    const onEnd = (v: number) => {                                 // Keep right of start; if window exceeds maxGap, push the start along.
        const e = clamp(Math.max(v, st.s + minGap));
        setState({e, s: maxGap === undefined ? st.s : Math.max(st.s, e - maxGap)});
    };
    const commit = () => onChange({start: st.s, end: st.e});

    const trackRef = useRef<HTMLDivElement>(null), drag = useRef<{x: number, s: number, win: number, w: number} | null>(null);
    const onBarDown = (e: React.PointerEvent) => {                 // Slide the whole window from the middle bar.
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = {x: e.clientX, s: st.s, win: st.e - st.s, w: trackRef.current!.getBoundingClientRect().width};
    };
    const onBarMove = (e: React.PointerEvent) => {
        if (!drag.current) return;
        const {x, s: s0, win, w} = drag.current;
        const delta = Math.round((e.clientX - x) / w * span / step) * step;
        const s = Math.min(Math.max(min, s0 + delta), max - win);                          // Keep the window width fixed.
        setState({s, e: s + win});
    };
    const onBarUp = (e: React.PointerEvent) => {
        if (!drag.current) return;
        drag.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
        commit();
    };

    const pct = (v: number) => ((v - min) / span) * 100;
    const rng = {min, max, step, onPointerUp: commit, onKeyUp: commit};

    return <div className={tailCheck(className, "flex flex-col", "w-[15rem]", "gap-1.5")}>
        <div ref={trackRef} className="relative flex items-center h-4">
            <div className="absolute inset-x-0 h-1 bg-handle rounded-full"/>
            <div className="absolute h-1 rounded-full bg-prm-light" style={{left: `${pct(st.s)}%`, right: `${100 - pct(st.e)}%`}}/>
            <div
                data-test={dataTest && `${dataTest}-window`} onPointerDown={onBarDown} onPointerMove={onBarMove} onPointerUp={onBarUp}
                className="absolute z-10 h-4 cursor-grab touch-none active:cursor-grabbing"
                style={{left: `calc(${pct(st.s)}% + 7px)`, right: `calc(${100 - pct(st.e)}% + 7px)`}}/>
            <input
                type="range" aria-label={`${dataTest ?? "range"} start`} data-test={dataTest && `${dataTest}-start`} value={st.s}
                onChange={e => onStart(+e.target.value)}
                className={`${rangeInputCls}${st.s > (min + max) / 2 ? " z-20" : ""}`} {...rng}/>
            <input
                type="range" aria-label={`${dataTest ?? "range"} end`} data-test={dataTest && `${dataTest}-end`} value={st.e}
                onChange={e => onEnd(+e.target.value)} className={rangeInputCls} {...rng}/>
        </div>
        {renderLabels &&
            <div className="flex justify-between text-2xs text-subtitle1">{renderLabels({start: st.s, end: st.e})}</div>}
    </div>;
}
